import { Box, Checkbox, FormLabel, IconButton, makeStyles, TextField, Theme, Typography } from "@material-ui/core"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { prettyEmptyOrNull } from "pure-shared"
import { COAAccountElement, GetInvoiceDto, FullMovementDto } from "services/api/openapi/models"
import { WorkspaceContext } from "services/context/workspaceContext"
import { useForm } from "react-hook-form"
import { useTypedController } from "@hookform/strictly-typed"
import BigNumber from "bignumber.js"
import { Mixpanel } from "services/mixpanel"
import { RestoreRounded } from "@material-ui/icons"
import api from "services/api"
import { GetChartAccountsTypesEnum } from "services/api/openapi"
import { DrawerProp } from "components/misc/useDrawer"
import { useLoadingButton } from "components/LoadingButton"
import PermissionDisabled from "components/Permission/PermissionDisabled"
import CustomSelector from "components/selector/CustomSelector"
import { DrawerCategory, DrawerFormSection } from "components/Drawer/DrawerItems"
import ChartAccountOptionInAutocomplete from "components/AutoCompleteOptions/ChartAccountOptionInAutocomplete"
import ConditionalTooltip from "components/ConditionalTooltip"
import { isLockedTransaction } from "services/utils/isLocked"
import TooltipUI from "CryptioUI/Tooltip"
import { useToast } from "CryptioUI/Toaster"
import { toastCatch } from "components/ReactHookForm/utils"

interface MovementUpdateFormAccounts {
  debitedAccount: COAAccountElement | null
  creditedAccount: COAAccountElement | null
}
interface MovementUpdateForm extends MovementUpdateFormAccounts {
  notes: string
  rate: string
  isManualMapping: boolean
}

interface COAAutocompleteData {
  displayName: string
  coaKey: keyof MovementUpdateFormAccounts
  // undefined means all
  acceptedType?: GetChartAccountsTypesEnum[]
}
const chartAccountAutocompletes: (mv: FullMovementDto) => COAAutocompleteData[] = (mv) => {
  // TODO: if trade: take other party symbol
  if (mv.accounting?.tradeTransactionId && mv.accounting.tradeAssetSymbol) {
    const incomingDebitedName = `${prettyEmptyOrNull(
      mv.direction === "in" ? mv.assetSymbol : mv.accounting.tradeAssetSymbol,
    )} debited account`
    const outgoingCreditedName = `${prettyEmptyOrNull(
      mv.direction === "out" ? mv.assetSymbol : mv.accounting.tradeAssetSymbol,
    )} credited account`
    return [
      {
        displayName: incomingDebitedName,
        coaKey: "debitedAccount",
        acceptedType: ["asset", "liability"],
      },
      {
        displayName: outgoingCreditedName,
        coaKey: "creditedAccount",
        acceptedType: ["asset", "liability"],
      },
    ]
  }

  if (mv.isFee) {
    return [
      {
        displayName: "Fee credited account",
        coaKey: "creditedAccount",
        acceptedType: ["asset"],
      },
      {
        displayName: "Fee debited account",
        coaKey: "debitedAccount",
        acceptedType: ["expense"],
      },
    ]
  }

  if (mv.direction === "in") {
    return [
      {
        displayName: "Debited account",
        coaKey: "debitedAccount",
        acceptedType: ["asset"],
      },
      {
        displayName: "Credited account",
        coaKey: "creditedAccount",
        acceptedType: mv.isInternalTransfer ? undefined : ["income", "liability", "equity"],
      },
    ]
  }
  return [
    {
      displayName: "Credited account",
      coaKey: "creditedAccount",
      acceptedType: ["asset"],
    },
    {
      displayName: "Debited account",
      coaKey: "debitedAccount",
      acceptedType: mv.isInternalTransfer ? undefined : ["expense", "liability", "equity"],
    },
  ]
}

const useStyles = makeStyles((theme: Theme) => ({
  validateButton: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
  invoiceTypography: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  invoiceBox: {
    cursor: "pointer",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
  },
  invoiceBoxDownload: {
    "&:hover > .MuiTypography-root": {
      textDecoration: "underline",
    },
  },
  checkboxManualMapping: {
    paddingLeft: 0,
  },
}))

const MovementForm = ({ item: movement, isOpen }: DrawerProp<FullMovementDto, false>) => {
  const classes = useStyles()

  const defaultFormValues: MovementUpdateForm = useMemo(
    () => ({
      notes: movement.note ?? "",
      rate: movement.assetToFiatRate ?? "",
      isManualMapping: movement.accounting?.isManualMapping ?? false,
      debitedAccount: movement.accounting?.debitedAccount ?? null,
      creditedAccount: movement.accounting?.creditedAccount ?? null,
    }),
    [movement],
  )

  const { control, reset, handleSubmit, watch, formState } = useForm<MovementUpdateForm>({
    mode: "onChange",
    defaultValues: defaultFormValues,
  })
  const watchAllFields = watch()
  const isFormDirty = useMemo(
    () =>
      formState.isValid &&
      formState.isDirty &&
      (watchAllFields.notes !== movement.note ||
        watchAllFields.rate !== (movement.assetToUsdRate ?? "") ||
        (watchAllFields.isManualMapping !== undefined &&
          movement.accounting &&
          movement.accounting.isManualMapping !== watchAllFields.isManualMapping)),
    [
      formState.isValid,
      formState.isDirty,
      watchAllFields.notes,
      watchAllFields.rate,
      watchAllFields.isManualMapping,
      movement.note,
      movement.assetToUsdRate,
      movement.accounting,
    ],
  )

  const TypedController = useTypedController<MovementUpdateForm>({ control })
  const [setInvoiceTransaction] = useState<GetInvoiceDto | null>(null)
  const toast = useToast()

  const { mutateAsync: updateMovementsMutation } = api.transaction.useBulkUpdateMovements()

  const workspaceCtx = useContext(WorkspaceContext)

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues)
    }
  }, [isOpen, defaultFormValues, reset, setInvoiceTransaction])

  const [SaveTransactionButton, handleButtonCallback] = useLoadingButton()

  const onSubmit = useCallback(
    async (form: MovementUpdateForm) => {
      const isManualMapping = form.isManualMapping ?? movement.accounting?.isManualMapping ?? false

      try {
        if (form.rate && new BigNumber(form.rate).isNegative()) throw new Error("Rate cannot be a negative value")
        await updateMovementsMutation({
          movementBulkUpdateDto: {
            individualMovements: {
              movementIds: [movement.id],
            },
            fiatRate: form.rate !== movement.originalAssetToFiatRate && form.rate !== "" ? form.rate : undefined,
            resetRates: form.rate === movement.originalAssetToFiatRate ? true : undefined,
            note: form.notes,
            manualMapping: isManualMapping
              ? {
                  debitedAccountId: form.debitedAccount?.id ?? null,
                  creditedAccountId: form.creditedAccount?.id ?? null,
                }
              : undefined,
            resetManualMapping: !isManualMapping ? true : undefined,
            labelIdsToRemove: [],
            labelIdsToAdd: [],
          },
        })

        toast.open("Movement updated", { variant: "success" })
      } catch (e) {
        toastCatch(e, toast)
      }
      if (form.notes !== movement.note) Mixpanel.track("TransactionNoteEdited")
    },
    [movement, toast, updateMovementsMutation],
  )

  const isLocked = isLockedTransaction(
    workspaceCtx.workspace.lock.isEnabled,
    movement.transactionDate,
    workspaceCtx.workspace.lock.exclusiveEndDate,
  )

  return (
    <>
      <DrawerCategory
        component="form"
        onSubmit={handleButtonCallback(handleSubmit(onSubmit))}
        title="Movement settings"
      >
        <DrawerFormSection
          htmlFor="volume-textfield"
          name={`Volume ${workspaceCtx.workspace.defaultCurrencyName} rate`}
          mt={2}
        >
          <TypedController
            name="rate"
            defaultValue={movement.assetToFiatRate ?? ""}
            render={({ value, onChange }) => (
              <PermissionDisabled permission="can_modify_transaction" action="modify a transaction">
                <ConditionalTooltip
                  tooltipMessage={"The rate of a locked movement can't be edited"}
                  disabled={isLocked}
                >
                  <TextField
                    placeholder={value === "" ? "Missing rate" : undefined}
                    error={!!value && isNaN(Number(value))}
                    value={value}
                    disabled={isLocked}
                    onChange={(x) => onChange(x.target.value)}
                    InputProps={{
                      endAdornment: movement.originalAssetToFiatRate && movement.originalAssetToFiatRate !== value && (
                        <TooltipUI
                          content={
                            <Typography variant="body1">
                              Reset rate to {movement.originalAssetToFiatRate || "missing rate"}
                            </Typography>
                          }
                        >
                          <IconButton
                            style={{ padding: 0 }}
                            aria-label="reset"
                            onClick={() => onChange(movement.originalAssetToFiatRate)}
                          >
                            <RestoreRounded />
                          </IconButton>
                        </TooltipUI>
                      ),
                    }}
                    fullWidth
                  />
                </ConditionalTooltip>
              </PermissionDisabled>
            )}
          />
        </DrawerFormSection>

        {workspaceCtx.workspace.accountingIntegration !== null && (
          <Box mt={3}>
            <Box display="flex" alignItems="center">
              <TypedController
                name="isManualMapping"
                rules={{ required: false }}
                render={({ onChange, value, ...rest }) => (
                  <PermissionDisabled permission="can_modify_transaction" action="modify transaction">
                    <Checkbox
                      color="primary"
                      id="manual-mapping-checkbox"
                      className={classes.checkboxManualMapping}
                      onChange={() => onChange(!value)}
                      checked={value}
                      {...rest}
                    />
                  </PermissionDisabled>
                )}
              />
              <FormLabel htmlFor="manual-mapping-checkbox">Manual chart of accounts mapping</FormLabel>
            </Box>

            {chartAccountAutocompletes(movement).map((coaAuto) => (
              <Box mt={2} key={coaAuto.coaKey}>
                <TypedController
                  name={coaAuto.coaKey}
                  rules={{ required: false }}
                  defaultValue={movement.accounting?.[coaAuto.coaKey] ?? null}
                  render={({ ref: _ref, onChange, ...rest }) => (
                    <PermissionDisabled
                      placement="right"
                      permission="can_modify_transaction"
                      action="modify transaction"
                    >
                      <CustomSelector
                        {...rest}
                        onChange={(_, newValue) => onChange(newValue)}
                        getOptionLabel={(option) => option.name}
                        defaultPaginatedQueryProps={{
                          sortBy: "name",
                          sortDirection: "ascending",
                          types: workspaceCtx.workspace.areChartAccountMappingsRestricted
                            ? coaAuto.acceptedType
                            : undefined,
                        }}
                        label={coaAuto.displayName}
                        disabled={!watchAllFields.isManualMapping || !movement.accounting}
                        getOptionSelected={(option, value) => option.id === value.id}
                        usePaginatedQuery={api.chartAccount.useChartAccounts}
                        size="small"
                        filterSelectedOptions
                        renderOption={(option) => <ChartAccountOptionInAutocomplete account={option} />}
                      />
                    </PermissionDisabled>
                  )}
                />
              </Box>
            ))}
          </Box>
        )}

        <DrawerFormSection htmlFor="notes-textfield" name="Notes">
          <TypedController
            name="notes"
            defaultValue={movement.note ?? ""}
            render={(props) => (
              <PermissionDisabled permission="can_modify_transaction" action="modify a transaction">
                <TextField id="notes-textfield" {...props} multiline placeholder="Write anything..." fullWidth />
              </PermissionDisabled>
            )}
          />
        </DrawerFormSection>

        <Box marginTop={3}>
          <SaveTransactionButton disabled={!isFormDirty} className={classes.validateButton} type="submit">
            Save
          </SaveTransactionButton>
        </Box>
      </DrawerCategory>
    </>
  )
}

export default MovementForm
